'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Heart, MessageCircle, Repeat2 } from 'lucide-react';
import { TwitterVerifiedIcon, TwitterViewIcon } from '@/components/Icons';
import XIcon from '@/components/Icons/XIcon';
import { formatCompactValue } from '@/lib/utils/number';
import { cn } from '@/lib/utils';
import dayjs from 'dayjs';
import { formatTimeAgo } from '@/lib/utils/date';
import { useTranslation } from '@/lib/i18n';

export interface TwitterItem {
  username: string;
  avatar_url: string;
  user_id: string;
  is_blue_verified: boolean;
  verified_type: string | null;
  created_at: string;
  text_content: string;
  url: string;
  reply_count: number;
  retweet_count: number;
  like_count: number;
  view_count: number | string;
}

interface TwitterListProps {
  twitter?: TwitterItem[];
  initialCount?: number;
}

export default function TwitterList({ twitter, initialCount = 2 }: TwitterListProps) {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(initialCount);
  const tweets = twitter ?? [];
  const visibleTweets = useMemo(() => tweets.slice(0, visibleCount), [tweets, visibleCount]);
  const hasMore = visibleCount < tweets.length;

  if (tweets.length === 0) return null;

  return (
    <section className="mt-6">
      <div className="space-y-6">
        {visibleTweets.map((tweet) => (
          <TweetCard key={tweet.url || `${tweet.username}-${tweet.created_at}`} tweet={tweet} />
        ))}
      </div>
      {hasMore && (
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={() => setVisibleCount(tweets.length)}
            className="h-9 min-w-[124px] rounded-full bg-[#232323] px-8 text-sm text-[#B7B9BC] transition-colors hover:bg-[#303030] hover:text-white"
          >
            {t('More')}
          </button>
        </div>
      )}
    </section>
  );
}

function TweetCard({ tweet }: { tweet: TwitterItem }) {
  const tweetId = getTweetId(tweet.url);
  const profileUrl = `https://twitter.com/${tweet.username}`;
  const tweetUrl = normalizeTweetUrl(tweet.url);
  const contentRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    const content = contentRef.current;
    if (!content || expanded) return;

    const updateOverflow = () => {
      setIsOverflowing(content.scrollHeight > content.clientHeight + 1);
    };

    updateOverflow();
    const resizeObserver = new ResizeObserver(updateOverflow);
    resizeObserver.observe(content);

    return () => {
      resizeObserver.disconnect();
    };
  }, [expanded, tweet.text_content]);

  return (
    <article className="overflow-hidden rounded-lg p-4 bg-linear-to-b from-[#202020] to-[#0B0B0B]">
      <div className="">
        <div className="flex items-start gap-4">
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="size-12 shrink-0 overflow-hidden rounded-full bg-[#2A2A2A]"
            aria-label={`Open @${tweet.username} on Twitter`}
          >
            {tweet.avatar_url ? (
              <img
                src={tweet.avatar_url}
                alt={tweet.username}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-lg font-semibold text-white">
                {tweet.username.charAt(0).toUpperCase()}
              </span>
            )}
          </a>

          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex min-w-0 items-center gap-1.5">
              <a
                href={profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate font-semibold leading-5 text-white hover:underline"
              >
                {formatDisplayName(tweet.username)}
              </a>
              {tweet.is_blue_verified && <TwitterVerifiedIcon />}
            </div>
            <div className="mt-1 text-[13px] leading-5 text-[#7A7A7D]">
              @{tweet.username} · {formatTimeAgo(tweet.created_at)}
            </div>
          </div>
        </div>

        <p
          ref={contentRef}
          className={cn(
            'mt-5 whitespace-pre-wrap leading-6 text-white',
            !expanded && 'line-clamp-3',
          )}
        >
          {tweet.text_content}
        </p>
        {!expanded && isOverflowing && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="mt-1 inline-flex text-[15px] leading-6 text-[#A772FF] hover:underline"
          >
            {t('Read More')}
          </button>
        )}
      </div>

      <div className="border-t border-[#242424] pt-3 mt-5">
        <div className="grid grid-cols-5 items-center gap-4 text-[#858585]">
          <MetricLink href={tweetId ? intentUrl('tweet', tweetId) : tweetUrl} label="Reply">
            <MessageCircle size={15} />
            <span>{formatCompactValue(tweet.reply_count)}</span>
          </MetricLink>
          <MetricLink href={tweetId ? intentUrl('retweet', tweetId) : tweetUrl} label="Retweet">
            <Repeat2 size={15} />
            <span>{formatCompactValue(tweet.retweet_count)}</span>
          </MetricLink>
          <MetricLink href={tweetId ? intentUrl('like', tweetId) : tweetUrl} label="Like">
            <Heart size={17} />
            <span>{formatCompactValue(tweet.like_count)}</span>
          </MetricLink>
          <a
            href={tweetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm font-semibold transition-colors hover:text-white"
            aria-label={t('Open tweet views on Twitter')}
          >
            <TwitterViewIcon className="size-[15px]" />
            <span>{formatCompactValue(Number(tweet.view_count) || 0)}</span>
          </a>
          <div className="flex items-center">
            <div className="h-5 w-px bg-[#303030]" />
            <a
              href={tweetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex justify-center text-[#8D8D8D] transition-colors hover:text-white"
              aria-label={t('Open tweet on X')}
            >
              <XIcon />
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}

function MetricLink({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('{{label}} on Twitter', { label: t(label) })}
      className={cn(
        'flex items-center gap-2 text-sm font-semibold transition-colors hover:text-white',
        label === 'Like' && 'hover:text-[#F91880]',
      )}
    >
      {children}
    </a>
  );
}

function formatDisplayName(username: string) {
  return username
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getTweetId(url: string) {
  return url.match(/status\/(\d+)/)?.[1] ?? null;
}

function normalizeTweetUrl(url: string) {
  return url.replace('twitter.com', 'x.com');
}

function intentUrl(type: 'tweet' | 'retweet' | 'like', tweetId: string) {
  if (type === 'tweet') return `https://twitter.com/intent/tweet?in_reply_to=${tweetId}`;
  return `https://twitter.com/intent/${type}?tweet_id=${tweetId}`;
}
